import React from "react"

import { isSimulationListenable, LiveIndicator } from "@/simulation-rooms"
import { CopyableId, Tooltip, useDesignSystemTheme } from "@coval/ui"
import { addToast } from "@heroui/react"
import { AlertCircle, Copy } from "lucide-react"

import { seriesChipTones } from "@/components/charts/seriesPalette"
import type { RunRecord } from "@/types/api/RunRecord"
import type { RunRow, RunRowLike } from "@/types/RunRow"

import {
  COMPARE_BY_COLUMN_KEY,
  type RunResultsExplanationTable,
} from "../../hooks/useRunResultsColumnDefs"
import type { RunResultsMetricRenderAdaptersById } from "../../types"
import type { OutlierEvidenceBySimulationId } from "../../utils/buildOutlierEvidence"
import { renderTableCell } from "../RenderTableCell"
import { AudioCell } from "./AudioCell"
import { ExpectedOutputJsonCell } from "./ExpectedOutputJsonCell"
import { OutlierExplanationTooltip } from "./OutlierExplanationTooltip"
import { StatusCell } from "./StatusCell"
import { TruncatedCell } from "./TruncatedCell"

type RunResultsTableRunRecord = Pick<RunRecord, "agent_id"> | null | undefined

function getTranscriptPreview(row: RunRowLike): string {
  return (
    (row as Partial<Pick<RunRow, "transcript">>).transcript?.[0]?.content ?? ""
  )
}

interface RunResultsCellProps {
  agentsById?: Record<string, { display_name: string }> | null
  columnKey: string
  compareColumnLabel: string | null
  explanationTable: RunResultsExplanationTable
  getCompareColor: (row: RunRowLike) => string | undefined
  getCompareDisplayValue: (row: RunRowLike) => string
  item: RunRowLike
  metricIdFilter: string | null
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
  mutationById: Map<string, Record<string, unknown>>
  mutationNameById: Map<string, string>
  /** When set, live rows show a listen affordance that opens the live-sims panel. */
  onListenLive?: (simulationOutputId: string) => void
  outlierEvidenceBySimulationId: OutlierEvidenceBySimulationId
  runRecord: RunResultsTableRunRecord
  scopedRowDisplayIndex: Map<RunRowLike, number>
  scopedRunRows: RunRowLike[]
}

type RunResultsCellRendererParams = Omit<RunResultsCellProps, "item">

export function createRunResultsCellRenderer(
  params: RunResultsCellRendererParams,
): (row: RunRowLike) => React.ReactNode {
  return function renderRunResultsCell(row: RunRowLike) {
    return <RunResultsCell {...params} item={row} />
  }
}

const RunResultsCell = React.memo(function RunResultsCell({
  agentsById,
  columnKey,
  compareColumnLabel,
  explanationTable,
  getCompareColor,
  getCompareDisplayValue,
  item,
  metricIdFilter,
  metricRenderAdaptersById,
  mutationById,
  mutationNameById,
  onListenLive,
  outlierEvidenceBySimulationId,
  runRecord,
  scopedRowDisplayIndex,
  scopedRunRows,
}: RunResultsCellProps) {
  const theme = useDesignSystemTheme()

  if (columnKey === "index") {
    const originalIndex = scopedRowDisplayIndex.get(item)
    return (originalIndex ?? scopedRunRows.indexOf(item)) + 1
  }

  if (columnKey in metricRenderAdaptersById) {
    const metricCell = renderTableCell(
      columnKey,
      item,
      metricRenderAdaptersById,
      scopedRunRows.indexOf(item),
    )
    const metricId = metricRenderAdaptersById[columnKey].id
    const outlierEvidence = item.simulation_output_id
      ? outlierEvidenceBySimulationId
          .get(item.simulation_output_id)
          ?.find((evidence) => evidence.metricId === metricId)
      : undefined

    if (!outlierEvidence) {
      return metricCell
    }

    const directionLabel =
      outlierEvidence.direction === "high" ? "↑ High" : "↓ Low"

    return (
      <div className="flex flex-col items-center gap-0.5">
        {metricCell}
        <span
          aria-label={`Outlier direction: ${outlierEvidence.direction}`}
          className="text-xs font-medium leading-none text-coval-warning-on-surface whitespace-nowrap"
        >
          {directionLabel}
        </span>
      </div>
    )
  }

  if (
    metricIdFilter != null &&
    metricIdFilter !== "" &&
    explanationTable.columns.some((c) => c.key === columnKey)
  ) {
    const value = explanationTable.getRowValues(item)[columnKey] ?? ""
    return value || "—"
  }

  if (columnKey === COMPARE_BY_COLUMN_KEY && compareColumnLabel) {
    const value = getCompareDisplayValue(item)
    const color = getCompareColor(item)
    const chip = color ? seriesChipTones(color) : null
    const showFirst6Only = !!color
    const displayText =
      value === "—"
        ? "—"
        : showFirst6Only && value.length > 6
          ? value.slice(0, 6)
          : value
    // Chip fill by theme: the ramp's `mid` tone with black text on the near-black
    // dark canvas; the pale `light` tint with `dark` text on the white surface.
    const cell = chip ? (
      <span
        className="text-sm font-medium truncate inline-block max-w-[180px] rounded px-1.5 py-0.5"
        style={
          theme === "dark"
            ? { backgroundColor: chip.mid, color: "#000" }
            : { backgroundColor: chip.light, color: chip.dark }
        }
      >
        {displayText}
      </span>
    ) : (
      <span
        className="text-coval-text text-sm truncate block max-w-[180px]"
        style={color ? { color } : undefined}
      >
        {displayText}
      </span>
    )
    if (
      value !== "—" &&
      (showFirst6Only ? value.length > 6 : value.length > 0)
    ) {
      return <Tooltip content={value}>{cell}</Tooltip>
    }
    return cell
  }

  switch (columnKey) {
    case "simulationOutputId": {
      const outlierEvidence = item.simulation_output_id
        ? outlierEvidenceBySimulationId.get(item.simulation_output_id)
        : undefined
      const rowNumber =
        (scopedRowDisplayIndex.get(item) ?? scopedRunRows.indexOf(item)) + 1
      return item.simulation_output_id ? (
        <div className="flex min-w-0 items-center gap-1">
          <CopyableId
            id={item.simulation_output_id}
            label="Copy simulation output ID"
          />
          {outlierEvidence && outlierEvidence.length > 0 && (
            <OutlierExplanationTooltip
              evidence={outlierEvidence}
              rowNumber={rowNumber}
            />
          )}
        </div>
      ) : (
        <span className="text-coval-text-muted">-</span>
      )
    }
    case "mutationName": {
      const name =
        item.mutation_name ??
        (item.mutation_id ? mutationNameById.get(item.mutation_id) : null)
      const overrides = item.mutation_id
        ? mutationById.get(item.mutation_id)
        : undefined
      const agentMetadata =
        runRecord?.agent_id && agentsById?.[runRecord.agent_id]
          ? (
              agentsById[runRecord.agent_id] as {
                display_name: string
                metadata?: unknown
              }
            ).metadata
          : undefined
      const tooltipJson = overrides ?? agentMetadata
      return (
        <Tooltip
          content={
            tooltipJson ? (
              <pre className="text-xs max-w-md max-h-64 overflow-auto whitespace-pre-wrap">
                {JSON.stringify(tooltipJson, null, 2)}
              </pre>
            ) : (
              (name ?? "Base")
            )
          }
        >
          <span className="text-coval-text text-sm truncate block max-w-[180px]">
            {name ?? "Base"}
          </span>
        </Tooltip>
      )
    }
    case "inputStr": {
      const displayValue = item.test_case_description?.trim()
        ? item.test_case_description
        : (item.input_str ?? "")
      return (
        <Tooltip
          content={
            <p className="text-sm whitespace-pre-wrap wrap-break-word max-w-md max-h-64 overflow-auto">
              {displayValue || "—"}
            </p>
          }
          side="top"
          align="start"
          className="max-w-md max-h-64 overflow-auto p-3"
        >
          <div className="min-w-0 max-w-full overflow-hidden cursor-default">
            <TruncatedCell value={displayValue} />
          </div>
        </Tooltip>
      )
    }
    case "expectedBehaviors":
      return (
        <TruncatedCell value={item.expected_behaviors?.join(" • ") || ""} />
      )
    case "expectedOutputJson":
      return <ExpectedOutputJsonCell value={item.expected_output_json} />
    case "transcript": {
      const hasError =
        item.error_status?.toUpperCase().includes("ERROR") ||
        item.error_status?.toUpperCase().includes("FAILED")

      if (hasError && item.error_message) {
        const sanitizedError = item.error_message.replace(/vapi/gi, "")
        return (
          <div
            className="flex items-center gap-2 text-coval-danger-text max-w-[200px]"
            onClick={(event) => event.stopPropagation()}
          >
            <AlertCircle className="w-4 h-4 shrink-0 text-coval-danger-text" />
            <Tooltip content={sanitizedError}>
              <span className="truncate text-sm flex-1">{sanitizedError}</span>
            </Tooltip>
            <Tooltip content="Click to copy">
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(sanitizedError)
                  addToast({
                    title: "Copied!",
                    description: "Error message copied to clipboard.",
                  })
                }}
                className="p-1 hover:bg-coval-danger-surface hover:text-coval-danger-on-surface rounded shrink-0 text-coval-danger-text"
                aria-label="Copy error message"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </Tooltip>
          </div>
        )
      }

      if (item.simulation_s3_bucket && item.simulation_s3_key) {
        return <AudioCell item={item} />
      }
      // A live row has no recording yet, so this slot is otherwise empty —
      // use it as the row-level entry point into the live-sims panel.
      if (
        onListenLive &&
        item.simulation_output_id &&
        isSimulationListenable(item)
      ) {
        const simulationOutputId = item.simulation_output_id
        return (
          <button
            type="button"
            aria-label="Listen to this live simulation"
            className="cursor-pointer"
            onClick={(event) => {
              event.stopPropagation()
              onListenLive(simulationOutputId)
            }}
          >
            <LiveIndicator size="sm" />
          </button>
        )
      }
      return <TruncatedCell value={getTranscriptPreview(item)} />
    }
    case "errorStatus":
      return <StatusCell value={item.error_status || ""} />
    default:
      return null
  }
})
